/**
 * Keystones d'ascension (Tier 2 — profondeur atelier). Nœuds de build : tu en **attunes
 * jusqu'à K** (gratuit, réversible — la profondeur est dans le choix, pas le grind). Chaque
 * keystone est un **arbitrage** de multiplicateurs combat appliqués côté client (DPS / or /
 * shiny / PV ennemis).
 *
 * Aligné `server/app/services/ascension_service.ts` (IDs + slots, verrouillé par tests).
 */
export type AscensionKeystoneId =
  | 'glass_cannon'
  | 'gold_rush'
  | 'collector'
  | 'endurance'
  | 'frenzy'
  | 'speedrunner'
  | 'banquier'

export type AscensionKeystoneEffect = {
  dpsMult: number
  goldMult: number
  shinyMult: number
  enemyHpMult: number
  /** Mult. kills requis par palier de frontière (<1 = clears plus rapides). Client-only. */
  killsPerStageMult: number
  /** Mult. coût des paliers du Puits à or (<1 = moins cher). Appliqué côté serveur (autoritaire). */
  goldWellCostMult: number
}

export type AscensionKeystoneDef = {
  id: AscensionKeystoneId
  labelFr: string
  labelEn: string
  descFr: string
  descEn: string
  effect: AscensionKeystoneEffect
}

const NO_EFFECT: AscensionKeystoneEffect = {
  dpsMult: 1,
  goldMult: 1,
  shinyMult: 1,
  enemyHpMult: 1,
  killsPerStageMult: 1,
  goldWellCostMult: 1,
}

export const ASCENSION_KEYSTONES: readonly AscensionKeystoneDef[] = [
  {
    id: 'glass_cannon',
    labelFr: 'Glass cannon',
    labelEn: 'Glass cannon',
    descFr: '+35 % DPS, mais +25 % PV ennemis',
    descEn: '+35% DPS, but +25% enemy HP',
    effect: { ...NO_EFFECT, dpsMult: 1.35, enemyHpMult: 1.25 },
  },
  {
    id: 'gold_rush',
    labelFr: 'Ruée vers l’or',
    labelEn: 'Gold rush',
    descFr: '+55 % or, mais −15 % DPS',
    descEn: '+55% gold, but −15% DPS',
    effect: { ...NO_EFFECT, goldMult: 1.55, dpsMult: 0.85 },
  },
  {
    id: 'collector',
    labelFr: 'Collectionneur',
    labelEn: 'Collector',
    descFr: '+60 % taux de shiny, mais −10 % or',
    descEn: '+60% shiny rate, but −10% gold',
    effect: { ...NO_EFFECT, shinyMult: 1.6, goldMult: 0.9 },
  },
  {
    id: 'endurance',
    labelFr: 'Endurance',
    labelEn: 'Endurance',
    descFr: '−22 % PV ennemis (clears plus rapides), mais −12 % or',
    descEn: '−22% enemy HP (faster clears), but −12% gold',
    effect: { ...NO_EFFECT, enemyHpMult: 0.78, goldMult: 0.88 },
  },
  {
    id: 'frenzy',
    labelFr: 'Frénésie',
    labelEn: 'Frenzy',
    descFr: '+20 % DPS, mais −20 % or',
    descEn: '+20% DPS, but −20% gold',
    effect: { ...NO_EFFECT, dpsMult: 1.2, goldMult: 0.8 },
  },
  {
    id: 'speedrunner',
    labelFr: 'Speedrunner',
    labelEn: 'Speedrunner',
    descFr: '−40 % de kills requis par palier (clears plus rapides), mais −15 % or',
    descEn: '−40% kills required per stage (faster clears), but −15% gold',
    effect: { ...NO_EFFECT, killsPerStageMult: 0.6, goldMult: 0.85 },
  },
  {
    id: 'banquier',
    labelFr: 'Banquier',
    labelEn: 'Banker',
    descFr: '−30 % coût du Puits à or, mais −10 % DPS',
    descEn: '−30% Gold well cost, but −10% DPS',
    effect: { ...NO_EFFECT, goldWellCostMult: 0.7, dpsMult: 0.9 },
  },
] as const

export const ASCENSION_KEYSTONE_IDS: readonly AscensionKeystoneId[] = ASCENSION_KEYSTONES.map(
  (k) => k.id
)

export const ASCENSION_KEYSTONE_SLOTS_MAX = 3
/** Niveaux d'ascension où un slot d'attunement s'ouvre (slot 1 débloqué à la 1ère ascension). */
export const ASCENSION_KEYSTONE_SLOT_TIERS = [1, 3, 8] as const

/** Nombre de keystones attunables simultanément, selon le niveau d'ascension. */
export function getAscensionKeystoneSlots(ascensionLevel: number): number {
  const lvl = Math.max(0, Math.floor(Number(ascensionLevel) || 0))
  let slots = 0
  for (const tier of ASCENSION_KEYSTONE_SLOT_TIERS) {
    if (lvl >= tier) slots++
  }
  return Math.min(ASCENSION_KEYSTONE_SLOTS_MAX, slots)
}

/** Effet combiné (produit) des keystones attunés. IDs inconnus/dupliqués ignorés. */
export function getCombinedKeystoneEffect(ids: ReadonlyArray<string>): AscensionKeystoneEffect {
  const byId = new Map(ASCENSION_KEYSTONES.map((k) => [k.id, k]))
  const seen = new Set<string>()
  const eff: AscensionKeystoneEffect = { ...NO_EFFECT }
  for (const id of ids ?? []) {
    if (seen.has(id)) continue
    const def = byId.get(id as AscensionKeystoneId)
    if (!def) continue
    seen.add(id)
    eff.dpsMult *= def.effect.dpsMult
    eff.goldMult *= def.effect.goldMult
    eff.shinyMult *= def.effect.shinyMult
    eff.enemyHpMult *= def.effect.enemyHpMult
    eff.killsPerStageMult *= def.effect.killsPerStageMult
    eff.goldWellCostMult *= def.effect.goldWellCostMult
  }
  return eff
}
